import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Progress,
    Text,
    Button,
    Input,
    Select,
    HStack,
    VStack,
    Icon,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Flex,
    useColorModeValue,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useToast,
    Center,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
} from '@chakra-ui/react';
import {
    SearchIcon,
    CheckCircleIcon,
    TimeIcon,
    EditIcon,
    WarningIcon,
    InfoIcon,
    CloseIcon
} from '@chakra-ui/icons';
import { fetchWrapper } from '../../../helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomBadge from '../../../components/SharedComponent/Badge';
import { FiUsers } from 'react-icons/fi';
import { BsDownload, BsEye, BsThreeDotsVertical } from 'react-icons/bs';
import Pagination from 'react-js-pagination';
// moment-timezone: this file formats with .tz(), which plain moment does not provide
import moment from 'moment-timezone';
import { getApiHeaders, getUserTimeZone, hasPermission } from '../../../utils/DefaultFunction';
import API from '../../../helpers/axiosInstance';
import { TbTimelineEventExclamation } from 'react-icons/tb';
import { LuMessageCircle, LuMessageSquare, LuMail, LuBell, LuPhone, LuHistory, LuGlobe } from 'react-icons/lu';
import CampaignScheduleModal from './CampaignScheduleModal';

const LOG_STATUS_META = {
    created: { color: '#00a63e', ring: '#dcfce7', title: 'Campaign Created' },
    scheduled: { color: '#155dfc', ring: 'var(--brand-tint-strong)', title: 'Campaign Scheduled' },
    started: { color: '#00a63e', ring: '#dcfce7', title: 'Campaign Started' },
    running: { color: '#00a63e', ring: '#dcfce7', title: 'Campaign Running' },
    resumed: { color: '#00a63e', ring: '#dcfce7', title: 'Campaign Resumed' },
    paused: { color: '#e17100', ring: '#fef3c7', title: 'Campaign Paused' },
    completed: { color: '#00a63e', ring: '#dcfce7', title: 'Campaign Completed' },
    cancelled: { color: '#e7000b', ring: '#fee2e2', title: 'Campaign Cancelled' },
    failed: { color: '#e7000b', ring: '#fee2e2', title: 'Campaign Failed' },
};

const getLogStatusMeta = (status) => {
    const key = (status || '').toLowerCase();
    return LOG_STATUS_META[key] || {
        color: '#64748b',
        ring: '#f1f5f9',
        title: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Update',
    };
};

const CHANNELS = [
    { id: 2, name: 'Whatsapp',            icon: LuMessageCircle, color: '#00a63e' },
    { id: 1, name: 'SMS',                 icon: LuMessageSquare, color: '#155dfc' },
    { id: 3, name: 'E-Mail',              icon: LuMail,          color: '#9810fa' },
    { id: 6, name: 'Push Notification',   icon: LuBell,          color: '#e60076' },
    { id: 5, name: 'In-App Notification', icon: LuBell,          color: '#4f39f6' },
    { id: 4, name: 'Voice (IVR)',         icon: LuPhone,         color: '#f54a00' },
];

const CampaignManagementTable = ({ handleNewCampaign }) => {
    // State management
    const [campaigns, setCampaigns] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Status');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 10 });

    // Delete confirmation dialog state
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const cancelRef = useRef();

    // Logs modal state
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [selectedCampaignForLogs, setSelectedCampaignForLogs] = useState(null);

    // Timezone list / reschedule modal
    const [scheduleModal, setScheduleModal] = useState({ isOpen: false, campaign: null, mode: 'view' });
    const openScheduleModal = (campaign, mode) => setScheduleModal({ isOpen: true, campaign, mode });
    const closeScheduleModal = () => setScheduleModal((prev) => ({ ...prev, isOpen: false }));

    const navigate = useNavigate();
    const toast = useToast();
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const location = useLocation();
    const [permissions, setPermissions] = useState([]);
    const [exportingId, setExportingId] = useState(null);


    const params = location.pathname.replace('/', "")

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${params}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const listPermission = hasPermission("campaign-list", permissions)
    const createPermission = hasPermission("campaign-create", permissions)
    const editPermission = hasPermission("campaign-edit", permissions)
    const deletePermission = hasPermission("campaign-delete", permissions)
    const reportPermission = hasPermission("campaign-report", permissions)
    const pauseCampaign = async (campaignId) => {
        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}/pause`
            const response = await fetchWrapper.post(url)

            if (response.success) {
                toast({
                    title: "Campaign Paused",
                    description: "The campaign has been paused successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                })

                loadCampaigns(currentPage)
                loadSummary()
            } else {
                throw new Error(response.message || "Failed to pause campaign")
            }
        } catch (err) {
            toast({
                title: "Error",
                description: err.message || "Failed to pause campaign",
                status: "error",
                duration: 5000,
                isClosable: true,
            })
        } finally {
            setLoading(false)
        }
    }

    const resumeCampaign = async (campaignId) => {
        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}/resume`
            const response = await fetchWrapper.post(url)

            if (response.success) {
                toast({
                    title: "Campaign Resumed",
                    description: "The campaign has been resumed successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                })

                loadCampaigns(currentPage)
                loadSummary()
            } else {
                throw new Error(response.message || "Failed to resume campaign")
            }
        } catch (err) {
            toast({
                title: "Error",
                description: err.message || "Failed to resume campaign",
                status: "error",
                duration: 5000,
                isClosable: true,
            })
        } finally {
            setLoading(false)
        }
    }
    const cancelCampaign = async (campaignId) => {
        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}/cancel`
            const response = await fetchWrapper.post(url)

            if (response.success) {
                toast({
                    title: "Campaign Cancelled",
                    description: "The campaign has been cancelled successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                })

                loadCampaigns(currentPage)
                loadSummary()
            } else {
                throw new Error(response.message || "Failed to cancel campaign")
            }
        } catch (err) {
            toast({
                title: "Error",
                description: err.message || "Failed to cancel campaign",
                status: "error",
                duration: 5000,
                isClosable: true,
            })
        } finally {
            setLoading(false)
        }
    }

    // Load campaigns list
    const loadCampaigns = async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '10',
                ...(statusFilter !== 'All Status' && { status: statusFilter.toLowerCase() }),
                ...(typeFilter !== 'All Types' && { campaign_type: typeFilter.toLowerCase() }),
                ...(searchQuery && { search: searchQuery })
            });

            const url = `${process.env.REACT_APP_API_URL}/campaigns?${params}`;
            const response = await fetchWrapper.get(url);

            if (response.success) {
                setCampaigns(response.data.campaigns || []);
                setPagination(response.data.pagination || { total: 0, pages: 1, limit: 10 });
            } else {
                throw new Error(response.message || 'Failed to load campaigns');
            }
        } catch (err) {
            console.error('Failed to load campaigns', err);
            setError(err.message || 'Failed to load campaigns');
        } finally {
            setLoading(false);
        }
    };

    // Load campaigns summary/analytics
    const loadSummary = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/campaigns/analytics/summary`;
            const response = await fetchWrapper.get(url);

            if (response.success) {
                setSummary(response.data);
            }
        } catch (err) {
            console.error('Failed to load campaign summary', err);
            // Don't set error for summary failure, just log it
        }
    };

    // Delete campaign function
    const deleteCampaign = async (campaignId) => {
        try {
            setIsDeleting(true);
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}`;
            const response = await fetchWrapper.delete(url);

            if (response.success) {
                // Show success toast
                toast({
                    title: 'Campaign deleted',
                    description: 'Campaign has been successfully deleted.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                // Reload campaigns to reflect the deletion
                loadCampaigns(currentPage);

                // Also reload summary to update counts
                loadSummary();
            } else {
                throw new Error(response.message || 'Failed to delete campaign');
            }
        } catch (err) {
            console.error('Failed to delete campaign', err);
            toast({
                title: 'Error',
                description: err.message || 'Failed to delete campaign',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
            setIsDeleteDialogOpen(false);
            setCampaignToDelete(null);
        }
    };

    // Handle delete confirmation
    const handleDeleteClick = (campaign) => {
        // Check if campaign is running - prevent deletion
        if (campaign.status.toLowerCase() === 'running') {
            toast({
                title: 'Cannot delete running campaign',
                description: 'Running campaigns cannot be deleted. Please stop the campaign first.',
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setCampaignToDelete(campaign);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (campaignToDelete) {
            deleteCampaign(campaignToDelete.id);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setCampaignToDelete(null);
    };

    const handleViewLogs = (campaign) => {
        setSelectedCampaignForLogs(campaign);
        setIsLogsOpen(true);
    };

    const handleCloseLogs = () => {
        setIsLogsOpen(false);
        setSelectedCampaignForLogs(null);
    };

    const isInitialMount = useRef(true);

    // Initial load
    useEffect(() => {
        loadCampaigns();
        loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload when filters change (skip on first mount to avoid double call)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const debounceTimer = setTimeout(() => {
            setCurrentPage(1);
            loadCampaigns(1);
        }, 500);

        return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, statusFilter, typeFilter]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        loadCampaigns(page);
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    };




    const getStatusIcon = (status) => {
        const normalized = status.toLowerCase();
        switch (normalized) {
            case "completed":
                return <CheckCircleIcon color="green.500" fontSize={"lg"} />;
            case "running":
                return <TimeIcon color="green.500" />;
            // a rescheduled campaign is still pending a send, so it reads like scheduled
            case "scheduled":
            case "re-scheduled":
            case "rescheduled":
                return <InfoIcon color="blue.500" />;
            case "draft":
                return <WarningIcon color="gray.500" />;
            case "failed":
                return <CloseIcon color="red.500" />;
            default:
                return <InfoIcon color="gray.500" />;
        }
    };

    // timezone_based campaigns store 'all' (or their first wave's zone) — moment cannot
    // resolve 'all' and would format "Invalid date", so fall back to the viewer's zone.
    const resolveCampaignZone = (campaign) => {
        const tz = campaign?.timezone;
        return tz && tz !== 'all' && moment.tz.zone(tz) ? tz : moment.tz.guess();
    };

    const getStatusBadge = (status) => {
        const normalizedStatus = status.toLowerCase();

        const statusConfig = {
            completed: { color: "green.800", bg: "#b9f8cf" },
            running: { color: "green.800", bg: "#b9f8cf" },
            scheduled: { color: "blue.800", bg: "blue.100" },
            // same treatment as scheduled — it is still waiting to send
            "re-scheduled": { color: "blue.800", bg: "blue.100" },
            rescheduled: { color: "blue.800", bg: "blue.100" },
            draft: { color: "gray.700", bg: "gray.200" },
            failed: { color: "red.800", bg: "red.100" },
        };

        const { color, bg } = statusConfig[normalizedStatus] || {
            color: "gray.700",
            bg: "gray.200",
        };

        return (
            <HStack spacing={2}>
                {getStatusIcon(status)}
                <CustomBadge
                    px={2}
                    py={.8}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="medium"
                    color={color}
                    bg={bg}
                    icon={getStatusIcon(status)}
                >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                </CustomBadge>
            </HStack>
        );
    };


    // Summary cards data
    const getSummaryCards = () => {
        if (!summary) return [];

        const totalCampaigns = summary.total_campaigns || 0;
        const statusCounts = {};
        let totalDelivered = 0;
        let totalSent = 0;

        if (summary.by_status) {
            summary.by_status.forEach(status => {
                statusCounts[status.status] = status.count;
                totalDelivered += status.total_delivered || 0;
                totalSent += status.total_sent || 0;
            });
        }

        const deliveryRate = totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(1) : 0;

        return [
            {
                label: 'Total Campaigns',
                value: totalCampaigns,
                icon: 'message'
            },
            {
                label: 'Running',
                value: statusCounts.running || 0,
                icon: 'play'
            },
            {
                label: 'Scheduled',
                value: statusCounts.scheduled || 0,
                icon: 'calendar'
            },
            {
                label: 'Completed',
                value: statusCounts.completed || 0,
                icon: 'check'
            },
            {
                label: 'Delivery Rate',
                value: `${deliveryRate}%`,
                icon: 'trending'
            }
        ];
    };

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle>Error loading campaigns!</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    const summaryCards = getSummaryCards();

    const handleEditCampaign = (id) => {
        navigate(`/campaign/${id}`)
    }
    const handleViewReport = (id) => {
        navigate(`/campaign/reports/${id}`)
    }

    const handleExport = async (campaignId, campaignName) => {
        try {
            setExportingId(campaignId);
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}/analytics/messages/export`;
            const response = await API.get(url, { headers: getApiHeaders(), responseType: 'blob' });
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const safeName = (campaignName || `campaign-${campaignId}`).replace(/[^a-z0-9_\-\s]/gi, '').trim().replace(/\s+/g, '-');
            link.download = `${safeName}-messages.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Export failed', error);
        } finally {
            setExportingId(null);
        }
    };




    return (
        <>
            <Box className='box-style-card'>

                <Flex justify="space-between" align="center" mb={6}>
                    <Text fontSize="xl" fontWeight="semibold" color="gray.700">
                        Campaign Management
                    </Text>
                    {createPermission && (

                        <Button

                            color="white"
                            background={"var(--brand-primary)"}
                            size="sm"
                            borderRadius="md"
                            onClick={handleNewCampaign}
                        >
                            Launch New Campaign
                        </Button>
                    )}
                </Flex>
                {listPermission ? <>
                    <HStack spacing={4} mb={6}>
                        <Box position="relative" flex="1" maxW="100%">
                            <Input
                                placeholder="Search campaigns by name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                pl={10}
                                bg="gray.50"
                                border="1px"
                                borderColor="gray.200"
                            />
                            <SearchIcon
                                position="absolute"
                                left={3}
                                top="50%"
                                transform="translateY(-50%)"
                                color="gray.400"
                            />
                        </Box>

                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            w="150px"
                            bg="white"
                            border="1px"
                            borderColor="gray.200"
                        >
                            <option value="All Status">All Status</option>
                            <option value="completed">Completed</option>
                            <option value="running">Running</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="draft">Draft</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <Select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            w="150px"
                            bg="white"
                            border="1px"
                            borderColor="gray.200"
                        >
                            <option value="All Types">All Types</option>
                            <option value="marketing">Marketing</option>

                            <option value="informational">Informational</option>
                        </Select>
                    </HStack>


                    <Box
                        bg="white"
                        borderRadius="lg"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                        boxShadow="sm"
                    >
                        {loading ? (
                            <Box textAlign="center" py={10}>
                                <Spinner size="xl" color="blue.500" />
                                <Text mt={4} color="gray.500">Loading campaigns...</Text>
                            </Box>
                        ) : (
                            <Table variant="simple">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600" minW={"180px"}>Campaign</Th>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600">Type</Th>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600">Channels</Th>

                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600">Status</Th>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600" w={"200px"}>Target Audience</Th>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600">Progress</Th>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600"  w={"200px"}>Performance</Th>
                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600">Created</Th>

                                        <Th py={2} fontSize="xs" fontWeight="600" color="black.600">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {campaigns?.map((campaign) => (
                                        <Tr key={campaign.id} _hover={{ bg: 'gray.50' }}>
                                            <Td py={4} whiteSpace="normal" wordBreak="break-word">
                                                <VStack align="start" spacing={1}>
                                                    <Text fontWeight="600" fontSize={"md"} color="gray.900">
                                                        {campaign.name}
                                                    </Text>
                                                    <Text fontSize="sm" color="gray.600" fontWeight="400">
                                                        Template: {campaign.template?.name || 'No Template'}
                                                    </Text>
                                                </VStack>
                                            </Td>

                                            <Td py={4}>
                                                <CustomBadge bg="white" py={"1.2"} style={{ borderRadius: "15px", border: "1px solid #0000001a" }} fontSize='xs' color='black'>{campaign.campaign_type}</CustomBadge>
                                            </Td>

                                            <Td py={4}>
                                                <HStack spacing={2}>
                                                    {(campaign.selected_channels || []).map((chId) => {
                                                        const ch = CHANNELS.find(c => c.id == chId);
                                                        if (!ch) return null;
                                                        return (
                                                            <Tooltip key={chId} label={ch.name} hasArrow placement="top">
                                                                <span>
                                                                    <ch.icon size={18} color={ch.color} />
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </HStack>
                                            </Td>

                                            <Td py={4} w={"200px"}>
                                                {getStatusBadge(campaign.status)}
                                                {(campaign?.launch_type === "scheduled" && campaign?.status !== "completed") && (
                                                    <>
                                                        <Text fontSize="xs" color="gray.500">{moment
                                                            .utc(campaign?.scheduled_at)
                                                            .tz(resolveCampaignZone(campaign))
                                                            .format("DD MMM YY hh:mm:ss A")}</Text>
                                                        <Text fontSize="xs" color="gray.500">{campaign?.timezone}</Text>
                                                        {/* per-timezone campaigns fire in waves — the full list is in the modal */}
                                                        {campaign?.timezone_based === "Y" && (
                                                            <Button
                                                                variant="link"
                                                                size="xs"
                                                                colorScheme="purple"
                                                                fontWeight="500"
                                                                onClick={() => openScheduleModal(campaign, 'view')}
                                                            >
                                                                View timezones
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </Td>

                                            <Td py={4}>
                                                <HStack>
                                                    <FiUsers fontSize={"20"} color='#64748b' />
                                                    <Text fontWeight="500">
                                                        {(campaign.total_audience || 0).toLocaleString()}
                                                    </Text>
                                                </HStack>
                                            </Td>

                                            <Td py={4}>
                                                <VStack align="start" spacing={1}>
                                                    <Flex fontSize="sm" fontWeight="500">
                                                        <Text fontSize="sm" color={"black"} fontWeight="500">{(campaign.total_sent || 0).toLocaleString()} </Text> / <Text fontSize="sm" color={"gray.400"} fontWeight="500">{(campaign.total_audience || 0).toLocaleString()}</Text>
                                                    </Flex>
                                                    <Progress
                                                        value={campaign.total_audience > 0 ? (campaign.total_sent / campaign.total_audience) * 100 : 0}
                                                        size="sm"
                                                        sx={{ "& > div": { background: "var(--brand-primary)" } }}
                                                        w="120px"
                                                        borderRadius="full"
                                                    />
                                                </VStack>
                                            </Td>

                                            <Td py={4}>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="md">
                                                        <Text as="span" fontWeight="400">Total:</Text> {(campaign.total_audience || 0).toLocaleString()}
                                                    </Text>
                                                    <Text fontSize="md">
                                                        <Text as="span" fontWeight="400">Sent:</Text> {(campaign.total_sent || 0).toLocaleString()}
                                                    </Text>
                                                    <Text fontSize="md">
                                                        <Text as="span" fontWeight="400">Delivered:</Text> {(campaign.total_delivered || 0).toLocaleString()}
                                                    </Text>
                                                   
                                                </VStack>
                                            </Td>

                                            <Td py={4} w={"200px"}>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="xs" fontWeight="500">
                                                        {moment
                                                            .utc(campaign.created_at)
                                                            .tz(getUserTimeZone())
                                                            .format("DD MMM YY hh:mm:ss A")}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        by {campaign.created_by}
                                                    </Text>
                                                </VStack>
                                            </Td>

                                            <Td py={4}>
                                                {(reportPermission || deletePermission || editPermission) && (
                                                    <Menu>
                                                        <MenuButton
                                                            as={IconButton}
                                                            icon={<BsThreeDotsVertical />}
                                                            variant="ghost"
                                                            size="sm"
                                                        />
                                                        <MenuList>
                                                            <MenuItem icon={<Icon as={LuHistory} />} onClick={() => handleViewLogs(campaign)}>
                                                                View Logs
                                                            </MenuItem>

                                                            {/* Timezone list is only meaningful for per-timezone campaigns,
                                                                and is readable at any status. */}
                                                            {campaign?.timezone_based === "Y" && (
                                                                <MenuItem icon={<Icon as={LuGlobe} />} onClick={() => openScheduleModal(campaign, 'view')}>
                                                                    Timezone Schedule
                                                                </MenuItem>
                                                            )}


                                                            {(campaign?.status === "draft" || campaign?.status === "scheduled") && (
                                                                <>
                                                                    {/* A timezone_based campaign already has its per-timezone waves
                                                                        scheduled server-side, so it is not editable once scheduled —
                                                                        drafts still are, nothing has been locked in yet. */}
                                                                    {editPermission && !(campaign?.timezone_based === "Y" && campaign?.status !== "draft") && (
                                                                        <MenuItem icon={<EditIcon />} onClick={() => handleEditCampaign(campaign.id)}>
                                                                            Edit Campaign
                                                                        </MenuItem>
                                                                    )}
                                                                    {deletePermission && (
                                                                        <MenuItem
                                                                            icon={<Icon viewBox="0 0 24 24" color="red.500">
                                                                                <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                                                                            </Icon>}
                                                                            color="red.500"
                                                                            onClick={() => handleDeleteClick(campaign)}
                                                                            isDisabled={campaign.status.toLowerCase() === 'running'}
                                                                        >
                                                                            Delete
                                                                        </MenuItem>
                                                                    )}
                                                                </>
                                                            )}

                                                            {/* A timezone_based campaign sends in waves, so earlier zones can
                                                                already have gone out while the campaign is still 'scheduled'
                                                                waiting on the next wave — its report is worth reading at any status. */}
                                                            {(campaign?.timezone_based === "Y"
                                                                || campaign?.status === "completed"
                                                                || campaign?.status === "cancelled"
                                                                || campaign.status === "running"
                                                                || campaign.status === "paused") && (
                                                                <>{reportPermission && (
                                                                    <MenuItem icon={<Icon><BsEye size={24} /></Icon>} onClick={() => handleViewReport(campaign.id)}>
                                                                        View Reports
                                                                    </MenuItem>
                                                                )}</>
                                                            )}

                                                            {campaign?.status === "completed" && (
                                                                <MenuItem
                                                                    icon={exportingId === campaign.id ? <Spinner size="xs" /> : <Icon><BsDownload size={24} /></Icon>}
                                                                    onClick={() => handleExport(campaign.id, campaign.name)}
                                                                    isDisabled={exportingId === campaign.id}
                                                                >
                                                                    {exportingId === campaign.id ? 'Exporting...' : 'Export Messages'}
                                                                </MenuItem>
                                                            )}

                                                            {campaign.status === "running" && (
                                                                <MenuItem icon={<TbTimelineEventExclamation />} onClick={() => pauseCampaign(campaign.id)}>
                                                                    Pause Campaign
                                                                </MenuItem>
                                                            )}

                                                            {campaign.status === "paused" && (
                                                                <MenuItem icon={<CheckCircleIcon />} onClick={() => resumeCampaign(campaign.id)}>
                                                                    Resume Campaign
                                                                </MenuItem>
                                                            )}

                                                            {((campaign.status === "running" || campaign?.status === "draft" || campaign?.status === "scheduled") && campaign?.status !== "scheduled") && (
                                                                <MenuItem icon={<CheckCircleIcon />} onClick={() => cancelCampaign(campaign.id)}>
                                                                    Cancel Campaign
                                                                </MenuItem>
                                                            )}
                                                        </MenuList>
                                                    </Menu>
                                                )}
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        )}
                    </Box>

                    {!loading && pagination.total > pagination.limit && (
                        <Flex justify="center" mt={6}>
                            <Pagination
                                activePage={currentPage}
                                itemsCountPerPage={pagination.limit}
                                totalItemsCount={pagination.total}
                                pageRangeDisplayed={5}
                                onChange={handlePageChange}
                                itemClass="page-item"
                                linkClass="page-link"
                                activeClass="active"
                                activeLinkClass="active-link"

                                disabledClass="disabled"

                            />
                        </Flex>
                    )}
                    {!loading && campaigns.length === 0 && (
                        <Box textAlign="center" py={10}>
                            <Text color="gray.500">No campaigns found matching your criteria.</Text>
                        </Box>
                    )}
                </> : <Box className='card-style'>
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </Box>}

            </Box>
            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteDialogOpen}
                leastDestructiveRef={cancelRef}
                onClose={handleCancelDelete}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Campaign
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <VStack align="start" spacing={3}>
                                <Text>
                                    Are you sure you want to delete this campaign? This action cannot be undone.
                                </Text>
                                {campaignToDelete && (
                                    <Box
                                        p={3}
                                        bg="gray.50"
                                        borderRadius="md"
                                        border="1px"
                                        borderColor="gray.200"
                                        w="full"
                                    >
                                        <Text fontWeight="600" fontSize="sm" color="gray.700">
                                            Campaign: {campaignToDelete.name}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            ID: #{campaignToDelete.id}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            Status: {campaignToDelete.status}
                                        </Text>
                                    </Box>
                                )}
                            </VStack>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={handleCancelDelete}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleConfirmDelete}
                                ml={3}
                                isLoading={isDeleting}
                                loadingText="Deleting..."
                            >
                                Delete Campaign
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Campaign Logs Modal */}
            <Modal isOpen={isLogsOpen} onClose={handleCloseLogs} size="lg" scrollBehavior="inside" isCentered>
                <ModalOverlay bg="blackAlpha.400" />
                <ModalContent borderRadius="xl" overflow="hidden">
                    <ModalHeader borderBottom="1px solid" borderColor="gray.100" py={4}>
                        <HStack spacing={3}>
                            <Flex
                                align="center"
                                justify="center"
                                w="36px"
                                h="36px"
                                borderRadius="full"
                                bg="blue.50"
                                flexShrink={0}
                            >
                                <Icon as={LuHistory} color="blue.500" boxSize={4} />
                            </Flex>
                            <Box>
                                <Text fontSize="md" fontWeight="700" color="gray.800">Campaign Logs</Text>
                                {selectedCampaignForLogs && (
                                    <Text fontSize="xs" fontWeight="400" color="gray.500">
                                        {selectedCampaignForLogs.name}
                                    </Text>
                                )}
                            </Box>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton top={4} />
                    <ModalBody py={6}>
                        {selectedCampaignForLogs?.logs?.length > 0 ? (
                            <VStack align="stretch" spacing={0}>
                                {selectedCampaignForLogs.logs.map((log, index) => {
                                    const meta = getLogStatusMeta(log.status);
                                    const isLast = index === selectedCampaignForLogs.logs.length - 1;
                                    return (
                                        <Flex key={index}>
                                            <VStack spacing={0} mr={4} w="10px" flexShrink={0}>
                                                <Box
                                                    w="10px"
                                                    h="10px"
                                                    borderRadius="full"
                                                    bg={meta.color}
                                                    boxShadow={`0 0 0 4px ${meta.ring}`}
                                                    mt="6px"
                                                    flexShrink={0}
                                                />
                                                {!isLast && <Box w="2px" flex="1" bg="gray.200" mt={2} mb={1} />}
                                            </VStack>
                                            <Box pb={isLast ? 0 : 7} flex="1">
                                                <Flex justify="space-between" align="baseline" gap={3}>
                                                    <Text fontWeight="600" fontSize="sm" color="gray.800">
                                                        {meta.title}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                                                        {moment
                                                            .utc(log.created_at)
                                                            .tz(getUserTimeZone())
                                                            .format("DD MMM YY, hh:mm A")}
                                                    </Text>
                                                </Flex>
                                                <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                    by {log.user}
                                                </Text>
                                                {log.reason && (
                                                    <Box mt={2} px={3} py={2} bg="gray.50" borderRadius="md">
                                                        <Text fontSize="sm" color="gray.600">{log.reason}</Text>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Flex>
                                    );
                                })}
                            </VStack>
                        ) : (
                            <VStack py={10} spacing={3}>
                                <Flex
                                    align="center"
                                    justify="center"
                                    w="48px"
                                    h="48px"
                                    borderRadius="full"
                                    bg="gray.50"
                                >
                                    <Icon as={LuHistory} boxSize={5} color="gray.300" />
                                </Flex>
                                <Text color="gray.500" fontSize="sm">No logs available for this campaign.</Text>
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Read-only timezone list for the selected campaign */}
            <CampaignScheduleModal
                campaign={scheduleModal.campaign}
                mode={scheduleModal.mode}
                isOpen={scheduleModal.isOpen}
                onClose={closeScheduleModal}
                onSaved={() => loadCampaigns(currentPage)}
            />
        </>
    );
};

export default CampaignManagementTable;